import React from 'react'
import { sum } from 'lodash'
import googlePalette from 'google-palette'
import { Trans, t } from '@lingui/macro'

import './UserInsightsBox.less'
import classNames from 'classnames'
import { ChartType } from 'chart.js'

import IconBubbles from '../../../../../components/common/icon/IconBubbles'
import { UserInsightsBoxBarChart } from './UserInsightsBoxBarChart'
import { ButtonOutline } from 'front-end/src/components'
import IconPlus2 from '../../../../../components/common/icon/IconPlus2'
import { linkBuilder } from 'shared/linkBuilder'
import { DashboardSummaryQuery } from '../../DashboardSummary.codegen'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import { DASHBOARD_TABS } from 'front-end/src/pages/dashboards-page/manage/ManageDashboardPage'

interface IUserInsightsBoxProps {
  surveys: NonNullable<
    NonNullable<DashboardSummaryQuery['admin']>['dashboard']
  >['surveys']
  userInsights: NonNullable<
    NonNullable<DashboardSummaryQuery['admin']>['dashboard']
  >['userInsights']
  dashboardId: number
}

export const fullWidthLimitOfSurveys = 6
export const UserInsightsBox = ({
  surveys,
  userInsights,
  dashboardId
}: IUserInsightsBoxProps) => {
  const answers = userInsights.reduce((accumulator, value) => {
    accumulator[value.surveyTitle] = accumulator[value.surveyTitle] || 0
    accumulator[value.surveyTitle] += 1
    return accumulator
  }, {} as { [key: string]: number })

  const surveyAnswers = surveys.map((s) => ({
    title: s.title,
    count: answers[s.title] ? answers[s.title] : 0
  }))

  const sumOfSurveys = sum(surveyAnswers.map((item) => item.count))
  const widthOfColumn = 30
  const maxWidthOfChart =
    surveyAnswers.length * (2 * widthOfColumn) + widthOfColumn
  const isFullWidthBox = surveyAnswers.length > fullWidthLimitOfSurveys

  const noLegend = surveyAnswers.length > 12
  const spaceSavingMode = surveyAnswers.length > 20

  const graphOptions = {
    responsive: true,
    barRoundness: 1,
    barWidth: !spaceSavingMode && widthOfColumn,
    gridLines: {
      display: false
    },
    hover: { mode: surveyAnswers.length ? 'intersect' : null },
    tooltips: {
      enabled: surveyAnswers.length ? true : false,
      callbacks: {
        label: (tooltipItem) => {
          return t`Answered by ${tooltipItem.value} people`
        }
      },
      titleFontSize: 15,
      bodyFontSize: 12,
      xPadding: 10,
      yPadding: 10,
      displayColors: false
    },
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        {
          ticks: {
            display: false
          },
          gridLines: {
            display: true,
            color: 'transparent',
            drawBorder: false
          }
        }
      ],
      xAxes: [
        {
          gridLines: {
            display: false
          },
          ticks: {
            display: false
          }
        }
      ]
    }
  }

  // Basic color palette
  const colorPalette = [
    'FFC402',
    'FF8B5C',
    'FF69AE',
    'D27BEF',
    '0091FF',
    '4BEBF0',
    '00D5B5',
    '6ADA14'
  ]

  // If needed extend the palette with generator
  if (surveyAnswers.length > colorPalette.length) {
    colorPalette.push(
      ...googlePalette('mpn65', surveyAnswers.length - colorPalette.length)
    )
  }

  return (
    <div
      className={classNames('DataSummaryGrid__item', {
        'DataSummaryGrid__item--big': isFullWidthBox,
        'DataSummaryGrid__item--noLegend': noLegend,
        'DataSummaryGrid__item--spaceSavingMode': spaceSavingMode,
        'DataSummaryGrid__item--placeholder': surveyAnswers.length === 0
      })}
    >
      <div className="Box UserInsightsBox">
        <div className="UserInsightsBox__data">
          <div className="UserInsightsBox__title">
            <IconBubbles />
            <div>
              <div className="UserInsightsBox__title__text">
                <Trans>Surveys</Trans>
              </div>
              <div className="UserInsightsBox__title__number">
                {sumOfSurveys}
              </div>
            </div>
          </div>
          {!!surveyAnswers.length ? (
            <ul className="UserInsightsBox__legend">
              {surveyAnswers.map((survey, index) => (
                <li key={index}>
                  <div className="UserInsightsBox__legend__number">
                    {survey.count}
                  </div>
                  <div
                    className="UserInsightsBox__legend__colorBox"
                    style={{ backgroundColor: `#${colorPalette[index]}` }}
                  />
                  <div className="UserInsightsBox__legend__text">
                    {survey.title}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="UserInsightsBox__hint">
              <div className="UserInsightsBox__hint__text">
                <Trans>Compare your surveys here</Trans>
              </div>
              <ButtonOutline
                onClick={() =>
                  navigateTo(
                    linkBuilder.dashboardManage(dashboardId, DASHBOARD_TABS[1])
                  )
                }
                leftIcon={<IconPlus2 />}
              >
                <Trans>Add surveys</Trans>
              </ButtonOutline>
            </div>
          )}
        </div>
        <div
          className="UserInsightsBox__chart"
          style={{
            maxWidth:
              !spaceSavingMode && surveyAnswers.length
                ? `${maxWidthOfChart}px`
                : undefined
          }}
        >
          {!!surveyAnswers.length ? (
            <UserInsightsBoxBarChart
              config={{
                type: 'roundedBar' as ChartType,
                // @ts-expect-error 9/11/2020
                options: graphOptions
              }}
              data={surveyAnswers}
              colorPalette={colorPalette}
            />
          ) : (
            <UserInsightsBoxBarChart
              width={400}
              height={100}
              config={{
                type: 'roundedBar' as ChartType,
                // @ts-expect-error 9/11/2020
                options: graphOptions
              }}
              data={[
                { title: 'a', count: 2 },
                { title: 'a', count: 4 },
                { title: 'a', count: 6 },
                { title: 'a', count: 8 },
                { title: 'a', count: 5 }
              ]}
            />
          )}
        </div>
      </div>
    </div>
  )
}
